import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchEmails } from '@/lib/api';
import type { Email } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn, capitalizeFirst } from '@/lib/utils';

interface EmailHistoryListProps {
  candidateId?: string;
  jobId?: string;
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'success';
    case 'pending':
    case 'queued':
      return 'warning';
    case 'failed':
    case 'bounced':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function formatEmailDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isYesterday) {
    return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EmailHistoryList({ candidateId, jobId }: EmailHistoryListProps) {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pageSize = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['emails', candidateId, jobId, page],
    queryFn: () => fetchEmails({ candidateId, jobId, page, limit: pageSize }),
    enabled: !!(candidateId || jobId),
  });

  const emails = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  function toggleExpand(id: string) {
    setExpandedId(expandedId === id ? null : id);
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="p-4 rounded-lg bg-bg-tertiary border border-white/10">
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3 bg-white/5" />
              <Skeleton className="h-3 w-1/3 bg-white/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="p-8 text-center bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <Mail className="h-10 w-10 text-text-tertiary mx-auto mb-3" />
        <h3 className="text-sm font-medium text-text-primary mb-1">No emails yet</h3>
        <p className="text-xs text-text-tertiary">
          Emails sent to this {candidateId ? 'candidate' : 'job contact'} will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {emails.map((email: Email) => {
        const isExpanded = expandedId === email.id;
        return (
          <div
            key={email.id}
            className="rounded-lg bg-bg-tertiary border border-white/10 overflow-hidden"
          >
            <button
              onClick={() => toggleExpand(email.id)}
              className="w-full text-left p-4 flex items-center gap-3 hover:bg-white/5 transition-colors"
            >
              <div className="flex-shrink-0 text-text-tertiary">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {email.subject}
                  </p>
                  <Badge variant={getStatusVariant(email.status)} className="flex-shrink-0 text-[10px]">
                    {capitalizeFirst(email.status)}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-xs text-text-tertiary truncate">
                    To: {email.toAddress}
                  </p>
                  {email.sentBy && (
                    <span className="text-xs text-text-tertiary">
                      &middot; by {email.sentBy.firstName} {email.sentBy.lastName}
                    </span>
                  )}
                </div>
              </div>

              <span className="text-xs text-text-tertiary flex-shrink-0 whitespace-nowrap">
                {formatEmailDate(email.sentAt || email.createdAt)}
              </span>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 pt-0 ml-7 border-t border-white/5">
                <div className="mt-3 space-y-2">
                  <div className="flex gap-4 text-xs text-text-tertiary">
                    <span>From: {email.fromAddress}</span>
                    <span>To: {email.toAddress}</span>
                  </div>
                  <div className={cn(
                    'text-sm text-text-secondary whitespace-pre-wrap rounded-lg p-3 bg-white/[0.02] border border-white/5',
                  )}>
                    {email.body}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-text-tertiary">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="text-xs h-7"
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="text-xs h-7"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
